import { useMutation, useQuery } from '@apollo/client';
import type { FlagQueryFilterForGrid } from '@generated/graphql';
import {
  type BulkTransitionFlagsForGridMutation,
  type FetchFlagsForGridQuery,
  FlagStates,
  FlagTransition,
} from '@generated/graphql';
import type { Tokens } from '@ovComponents/0-tokens/useTokens';
import type {
  DataTableTanstackTable,
  PaginationState,
  RowSelectionState,
  RowsPerPageOption,
} from '@ovComponents/2-component/dataTable';
import { useDataTable } from '@ovComponents/2-component/dataTable';
import { useGlobalToast } from '@providers/globalToastProvider';
import type { OnChangeFn } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildReconFlagColumns } from '../table/reconFlagsGrid.columns';
import {
  BULK_TRANSITION_FLAGS_FOR_GRID,
  FETCH_FLAGS_FOR_GRID,
} from './flags.queries';
import type { ReconFlagRow } from './reconFlags.types';
import {
  buildFetchFlagsGridVariables,
  shouldSuppressReconFlagsGridResults,
} from './reconFlagsGridVariables';

type UseReconFlagsGridInput = {
  sys: Tokens['sys'];
  t: TFunction;
  orgId: string | undefined;
  gridFilter: FlagQueryFilterForGrid | null;
  flagState: string;
  skipFlagsForAccountSearch: boolean;
  accountSearchInFlight: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: RowsPerPageOption;
  setPageSize: (size: RowsPerPageOption) => void;
  onRowClick: (row: ReconFlagRow) => void;
  refetchAfterFlagMutation: () => Promise<unknown>;
};

export const useReconFlagsGrid = ({
  sys,
  t,
  orgId,
  gridFilter,
  flagState,
  skipFlagsForAccountSearch,
  accountSearchInFlight,
  page,
  setPage,
  pageSize,
  setPageSize,
  onRowClick,
  refetchAfterFlagMutation,
}: UseReconFlagsGridInput): {
  gridProps: {
    sys: Tokens['sys'];
    table: DataTableTanstackTable<ReconFlagRow>;
    pageSize: RowsPerPageOption;
    tableLoading: boolean;
    pageCount: number;
    page: number;
    onPageChange: (p: number) => void;
    totalRowCount: number;
    onPerPageChange: (perPage: number) => void;
    onRowClick: (row: ReconFlagRow) => void;
    selectedCount: number;
    onSelectAllOnPage: () => void;
    onClearSelection: () => void;
    onBulkDismiss: () => Promise<boolean>;
    bulkDismissLoading: boolean;
    gridQueryLoading: boolean;
  };
  refetch: () => Promise<unknown>;
} => {
  const { showToast } = useGlobalToast();

  const gridFilterKey = useMemo(() => JSON.stringify(gridFilter), [gridFilter]);
  const [resolvedGridFilterKey, setResolvedGridFilterKey] = useState<
    string | null
  >(null);

  const gridQueryVariables = useMemo(
    () =>
      buildFetchFlagsGridVariables({
        gridFilter,
        pageSize,
        page,
        flagState,
      }),
    [gridFilter, pageSize, page, flagState],
  );

  const { loading, data, refetch, previousData } =
    useQuery<FetchFlagsForGridQuery>(FETCH_FLAGS_FOR_GRID, {
      variables: gridQueryVariables,
      skip: !orgId || skipFlagsForAccountSearch,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    setResolvedGridFilterKey(null);
  }, [gridFilterKey]);

  useEffect(() => {
    if (!skipFlagsForAccountSearch && !loading && data) {
      setResolvedGridFilterKey(gridFilterKey);
    }
  }, [skipFlagsForAccountSearch, loading, data, gridFilterKey]);

  const suppressGridResults = shouldSuppressReconFlagsGridResults(
    skipFlagsForAccountSearch,
    loading,
    gridFilterKey,
    resolvedGridFilterKey,
  );

  const serverFlags: ReconFlagRow[] = useMemo(() => {
    if (suppressGridResults) {
      return [];
    }
    const gridResult =
      loading && gridFilterKey !== resolvedGridFilterKey
        ? data
        : (data ?? previousData);
    return gridResult?.fetchFlagsForGrid?.flags ?? [];
  }, [
    suppressGridResults,
    data,
    previousData,
    loading,
    gridFilterKey,
    resolvedGridFilterKey,
  ]);

  const filteredGridTotalCount = useMemo(() => {
    if (suppressGridResults) {
      return 0;
    }
    const gridResult =
      loading && gridFilterKey !== resolvedGridFilterKey
        ? data
        : (data ?? previousData);
    return gridResult?.fetchFlagsForGrid?.totalCount ?? 0;
  }, [
    suppressGridResults,
    loading,
    gridFilterKey,
    resolvedGridFilterKey,
    data,
    previousData,
  ]);

  const bulkSelectionEnabled = flagState === FlagStates.Flagged;

  const columns = useMemo(
    () =>
      buildReconFlagColumns(t, sys, {
        enableBulkSelection: bulkSelectionEnabled,
      }),
    [t, sys, bulkSelectionEnabled],
  );
  const pageCount = Math.max(1, Math.ceil(filteredGridTotalCount / pageSize));

  const { table, rowSelection, setRowSelection } = useDataTable({
    data: serverFlags,
    columns,
    getRowId: (row) => row.id,
    enableRowSelection: bulkSelectionEnabled,
    paginationMode: 'manual',
    pageCount,
    pagination: {
      pageIndex: Math.max(0, page - 1),
      pageSize,
    },
    onPaginationChange: ((updater) => {
      const current: PaginationState = {
        pageIndex: Math.max(0, page - 1),
        pageSize,
      };
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize as RowsPerPageOption);
        setPage(1);
      } else if (next.pageIndex !== current.pageIndex) {
        setPage(next.pageIndex + 1);
      }
    }) as OnChangeFn<PaginationState>,
    rowsPerPage: pageSize,
    tableOptions: {
      defaultColumn: {
        enableSorting: false,
      },
    },
  });

  const selectedCount = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]).length,
    [rowSelection],
  );

  useEffect(() => {
    if (!bulkSelectionEnabled) {
      setRowSelection({});
    }
  }, [bulkSelectionEnabled, setRowSelection]);

  useEffect(() => {
    const valid = new Set(serverFlags.map((f) => f.id));
    setRowSelection((prev: RowSelectionState) => {
      const next: RowSelectionState = {};
      for (const id of Object.keys(prev)) {
        if (prev[id] && valid.has(id)) {
          next[id] = true;
        }
      }
      return next;
    });
  }, [serverFlags, setRowSelection]);

  const previousPageRef = useRef(page);
  useEffect(() => {
    if (previousPageRef.current === page) return;
    previousPageRef.current = page;
    setRowSelection({});
  }, [page, setRowSelection]);

  const clearSelection = useCallback(() => {
    setRowSelection({});
  }, [setRowSelection]);

  const selectAllVisible = useCallback(() => {
    table.toggleAllRowsSelected(true);
  }, [table]);

  const [bulkTransitionFlagsForGrid, bulkOptions] = useMutation(
    BULK_TRANSITION_FLAGS_FOR_GRID,
  );

  const dismissSelected = useCallback(async (): Promise<boolean> => {
    if (!bulkSelectionEnabled) {
      return false;
    }
    const selectedRows = table.getSelectedRowModel().rows;
    const ids = selectedRows
      .filter((r) => r.original.state === FlagStates.Flagged)
      .map((r) => r.original.id);
    if (ids.length === 0) {
      if (selectedRows.length > 0) {
        showToast({
          severity: 'warning',
          message: t('reconFlags.bulkDismissNoFlaggedSelected'),
        });
      }
      return false;
    }
    try {
      const { data: mutationData, errors } = await bulkTransitionFlagsForGrid({
        variables: {
          input: { flagIds: ids, transition: FlagTransition.Trivial },
        },
      });
      if (errors?.length) {
        showToast({
          severity: 'error',
          message: t('reconFlags.bulkDismissError'),
        });
        return false;
      }
      const failed = mutationData?.bulkTransitionFlagsForGrid?.failed ?? [];
      if (failed.length > 0) {
        showToast({
          severity: 'warning',
          message: t('reconFlags.bulkDismissPartial', {
            failed: failed.length,
            total: ids.length,
          }),
        });
        const failedIds = new Set(
          failed.map(
            (
              entry: BulkTransitionFlagsForGridMutation['bulkTransitionFlagsForGrid']['failed'][number],
            ) => entry.flagId,
          ),
        );
        setRowSelection((prev: RowSelectionState) => {
          const next: RowSelectionState = {};
          for (const id of Object.keys(prev)) {
            if (prev[id] && failedIds.has(id)) {
              next[id] = true;
            }
          }
          return next;
        });
        await refetchAfterFlagMutation();
        return false;
      }
      showToast({
        severity: 'success',
        message: t('reconFlags.bulkDismissSuccess', { count: ids.length }),
      });
      clearSelection();
      await refetchAfterFlagMutation();
      return true;
    } catch {
      showToast({
        severity: 'error',
        message: t('reconFlags.bulkDismissError'),
      });
      return false;
    }
  }, [
    bulkSelectionEnabled,
    table,
    bulkTransitionFlagsForGrid,
    clearSelection,
    setRowSelection,
    refetchAfterFlagMutation,
    showToast,
    t,
  ]);

  const tableLoading =
    suppressGridResults ||
    (loading && !previousData && gridFilterKey === resolvedGridFilterKey);

  const gridProps = useMemo(
    () => ({
      sys,
      table,
      pageSize,
      tableLoading,
      pageCount,
      page,
      onPageChange: setPage,
      totalRowCount: filteredGridTotalCount,
      onPerPageChange: (nextPerPage: number) => {
        setPageSize(nextPerPage as RowsPerPageOption);
        setPage(1);
      },
      onRowClick,
      selectedCount,
      onSelectAllOnPage: selectAllVisible,
      onClearSelection: clearSelection,
      onBulkDismiss: dismissSelected,
      bulkDismissLoading: bulkOptions.loading,
      gridQueryLoading: suppressGridResults || loading || accountSearchInFlight,
    }),
    [
      sys,
      table,
      pageSize,
      tableLoading,
      pageCount,
      page,
      setPage,
      filteredGridTotalCount,
      setPageSize,
      onRowClick,
      selectedCount,
      selectAllVisible,
      clearSelection,
      dismissSelected,
      bulkOptions.loading,
      suppressGridResults,
      loading,
      accountSearchInFlight,
    ],
  );

  return { gridProps, refetch };
};
